import { useState, type FormEvent } from "react";
import { api } from "../lib/api";
import { Notice } from "../components/Layout";
import { clearPendingProfile, readPendingProfile } from "./Auth";

/**
 * Shown once, right after a guest's first sign-in. Captures the phone number
 * that lets us match them to the imported guest list.
 */
export function Onboarding({ email, onDone }: { email: string; onDone: () => void }) {
  const pending = readPendingProfile();
  const [fullName, setFullName] = useState(pending.fullName ?? "");
  const [phone, setPhone] = useState(pending.phone ?? "");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!fullName.trim()) return setError("Please tell us your full name.");

    setBusy(true);
    try {
      await api.post("/api/profile", { fullName: fullName.trim(), phone: phone.trim() });
      clearPendingProfile();
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="animate-rise">
      <p className="eyebrow">Almost there</p>
      <h1 className="mt-4 font-display text-4xl leading-tight font-light sm:text-5xl">
        A couple of details
      </h1>
      <p className="mt-4 leading-relaxed text-muted">
        You're signed in as <span className="text-ink">{email}</span>. We just need your name and
        number to find you on the guest list.
      </p>

      <form onSubmit={onSubmit} className="mt-10 space-y-4">
        <div>
          <label htmlFor="ob-name" className="eyebrow mb-2 block">
            Full name
          </label>
          <input
            id="ob-name"
            className="field"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder="Jordan Rivera"
            autoComplete="name"
          />
        </div>

        <div>
          <label htmlFor="ob-phone" className="eyebrow mb-2 block">
            Mobile number
          </label>
          <input
            id="ob-phone"
            type="tel"
            className="field"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="(555) 123-4567"
            autoComplete="tel"
          />
          <p className="mt-2 text-xs text-muted">
            Optional, but it helps us find you if you signed up with a different email than the one
            on our list.
          </p>
        </div>

        {error && <Notice tone="error">{error}</Notice>}

        <button type="submit" className="btn btn-primary w-full" disabled={busy}>
          {busy ? "Saving…" : "Continue"}
        </button>
      </form>
    </div>
  );
}
